<!-- eslint-disable max-len -->
<template>
  <div>
    <h3 class="title">CREATE APPLICATION</h3>

    <div class="application-type">
      <div class="title-section">Application Type</div>
      <div class="types">
        <div
          v-for="mode in modes"
          :key="mode.id"
          :class="{ 'active': mode.id === modeSelected}"
          class="type"
          @click="modeSelected = mode.id"
        >
          <img :src="require(`@/assets/icon/${mode.id}.png`)">
          <span class="mode-option">{{ mode.text }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="modeSelected"
      class="form-create-app"
    >
      <!-- Icon -->
      <div class="form-section d-flex">
        <div
          :class="{'error-icon' : $v.application.icon_url.$error}"
          class="preview-icon"
        >
          <img
            v-if="application.icon_url"
            :src="application.icon_url"
          >
          <input
            ref="iconUpload"
            type="file"
            class="upload-photo"
            @change="handleIconUpload($event)"
          >
        </div>
        <div>
          <p class="title-config">Application Icon</p>
          <p class="desc-config">
            Icon must be minimum size 1024 x 1024px and .png format.
          </p>
          <p
            class="upload-btn"
            @click="$refs.iconUpload.click()"
          >
            Upload
          </p>
        </div>
      </div>

      <!-- General -->
      <div class="form-section">
        <div class="mb-2">
          <p class="title-config">General Information</p>
          <p class="desc-config">
            Information to describe the application
          </p>
        </div>

        <b-form-group
          :class="{'error-input' : $v.application.app_name.$error}"
          class="mb-1"
        >
          <label> Application Name </label>
          <j-input
            v-model="application.app_name"
            :error-required="false"
            placeholder="Enter application name"
          />
        </b-form-group>
        <b-form-group
          v-if="modeSelected === 'hyperlink'"
          :class="{'error-input' : $v.application.app_url.$error}"
          class="mb-1"
        >
          <label> URL </label>
          <j-input
            v-model="application.app_url"
            :error-required="false"
            placeholder="Enter url"
          />
        </b-form-group>
        <b-form-group
          :class="{'error-input' : $v.application.desc_name.$error}"
          class="mb-1"
        >
          <label> Description </label>
          <j-textarea
            v-model="application.desc_name"
            :error-required="false"
            placeholder="Enter application description"
          />
        </b-form-group>
        <b-form-group>
          <label> Group Application </label>
          <j-single-select
            v-model="selectedGroup"
            :options="groupOptions"
            :error-required="false"
            placeholder="Select Group"
            value-format="object"
          />
        </b-form-group>
        <div
          v-if="modeSelected === 'hyperlink'"
          class="d-flex justify-content-between align-items-center mt-3"
        >
          <label> Open Hyperlink in App Browser (Mobile) </label>
          <j-switch v-model="application.open_hyperlink_in_app_browser"/>
        </div>
        <div
          v-if="modeSelected === 'hyperlink'"
          class="d-flex justify-content-between align-items-center mt-3"
        >
          <label> Use Token </label>
          <j-switch v-model="application.use_token"/>
        </div>
        <div
          v-if="application.is_offline_mode"
          class="local-storage mt-3"
        >
          <img
            src="@/assets/logo/offline-mode.png"
            height="16"
          >
          Local Storage Support
        </div>
      </div>

      <!-- Search Menu -->
      <div class="form-section d-flex justify-content-between align-items-center">
        <div>
          <p class="title-config">Search Menu</p>
          <p class="desc-config">
            A search bar or search box in order to look up menu or page
          </p>
        </div>
        <j-switch v-model="application.search_menu" />
      </div>

      <!-- Display Mode -->
      <div class="form-section">
        <div class="mb-2">
          <p class="title-config">Display Mode</p>
          <p class="desc-config">
            Select the device that will be used to display this application
          </p>
        </div>

        <div class="d-flex align-items-center">
          <div
            v-for="option in listDisplayOptions"
            :key="option.id"
            :class="{ 'active': displayModeSelected.includes(option.id)}"
            class="btn-option"
            @click="handleDisplayMode(option.id)"
          >
            <div
              v-if="!displayModeSelected.includes(option.id)"
              class="circle-checkbox"
            />
            <img
              v-if="displayModeSelected.includes(option.id)"
              class="checked"
              src="@/assets/new-form-ui/icon-active/check-circle-active.svg"
            >
            <img
              :src="require(`@/assets/form-ui/${option.id}${displayModeSelected.includes(option.id) ? '-active' : ''}.svg`)"
              class="icon-btn"
            >
            <span>{{ option.text }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-content-end mt-4">
      <button
        class="button-close mr-2"
        @click="closeModal"
      >
        Close
      </button>
      <button
        :disabled="!modeSelected"
        class="button-create-app"
        @click="createApp"
      >
        Create App
      </button>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-nested-ternary, max-len, quotes */
import { mapState, mapActions } from 'vuex';
import { required, requiredIf } from 'vuelidate/lib/validators';
// Mixins
import jMixUploadFile from '@/mixins/jMixUploadFile';
// Components
import jSwitch from '@/components/inputs/jSwitch';

export default {
  validations() {
    return {
      application: {
        app_name: { required },
        app_url: { required: requiredIf(() => this.modeSelected === 'hyperlink') },
        desc_name: { required },
        is_offline_mode: {},
        open_hyperlink_in_app_browser: {},
        use_token: {},
        icon_url: { required },
      },
    };
  },

  components: {
    jSwitch,
  },

  mixins: [jMixUploadFile],

  data() {
    return {
      modes: [
        { id: 'native', text: 'Native' },
        { id: 'hyperlink', text: 'Hyperlink' },
      ],
      modeSelected: null,
      application: {
        app_name: null,
        app_url: null,
        desc_name: null,
        search_menu: false,
        is_offline_mode: false,
        open_hyperlink_in_app_browser: false,
        use_token: false,
        icon_url: null,
        show_option: 3,
      },
      selectedGroup: null,
      groupOptions: [],
      listDisplayOptions: [
        {
          id: 'web',
          text: 'Website',
        },
        {
          id: 'mobile',
          text: 'Mobile',
        },
      ],
      displayModeSelected: ['web', 'mobile'],
    };
  },

  computed: {
    ...mapState('jStoreSetting', ['group_applications_list']),
  },

  watch: {
    group_applications_list(val) {
      this.groupOptions = [];
      val.forEach((lab) => {
        this.groupOptions.push({
          text: lab.name,
          id: lab.id,
          is_delete: false,
        });
      });
    },

    displayModeSelected(val) {
      if (val.length === 0) this.application.show_option = 0;
      else if (val.length === 1 && val.includes('web')) this.application.show_option = 1;
      else if (val.length === 1 && val.includes('mobile')) this.application.show_option = 2;
      else if (val.length > 1) this.application.show_option = 3;
    },
  },

  created() {
    this.getGroupApplicationList();
  },

  methods: {
    ...mapActions('jStoreSetting', ['actionGetGroupApplicationsDetail', 'actionCreateApplication']),

    async getGroupApplicationList() {
      const param = {
        params: {
          page: 1,
          limit: 0,
          column: 'id',
          sort: 'asc',
          query: '',
          query_type: '',
        },
      };
      await this.actionGetGroupApplicationsDetail(param);
    },

    handleDisplayMode(type) {
      if (this.displayModeSelected.includes(type)) {
        this.displayModeSelected = this.displayModeSelected.filter(f => f !== type);
      } else {
        this.displayModeSelected.push(type);
      }
    },

    handleIconUpload(e) {
      this.handleFileUpload(e, ['image/png'], 5).then((res) => {
        this.application.icon_url = res;
      });
    },

    createApp() {
      this.$v.application.$touch();

      if (!this.$v.application.$error) {
        const text = this.modeSelected === 'native' ?
          `Offline Mode can't be updated after the application has successfully created`
          :
          '';
        const textMode = this.modeSelected === 'native' ?
          (this.application.is_offline_mode ? 'with Offline Mode'
            :
            'without Offline Mode'
          )
          : '';
        this.$swal({
          title: `Are you sure to create application ${textMode}?`,
          text,
          showCancelButton: true,
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
        }).then(async (result) => {
          if (result.value) {
            const parameter = {
              name: this.application.app_name,
              icon: this.application.icon_url,
              icon_launcher: '-',
              description: this.application.desc_name,
              order: 0,
              type_platform: this.modeSelected === 'hyperlink' ? (this.application.use_token ? 5 : 4) : 1,
              standalone_id: 0,
              hyperlink: this.modeSelected === 'hyperlink' ? this.application.app_url : '',
              group_application_id: this.selectedGroup ? this.selectedGroup.id : undefined,
              is_offline_mode: this.application.is_offline_mode,
              is_open_hyperlink_in_app_browser: this.application.open_hyperlink_in_app_browser,
              is_show_search_menu: this.application.search_menu,
              show_option: this.application.show_option,
            };
            const res = await this.actionCreateApplication(parameter);
            if (!res.error) {
              this.$router.push(`/application-manager/general/${res.data.id}`);
            }
          }
        });
      }
    },

    closeModal() {
      this.$emit('close-modal', false);
      this.resetData();
    },

    resetData() {
      this.modeSelected = null;
      this.application = {
        app_name: null,
        app_url: null,
        desc_name: null,
        is_offline_mode: false,
        open_hyperlink_in_app_browser: false,
        use_token: false,
        icon_url: null,
        show_option: 3,
      };
      this.displayModeSelected = ['web', 'mobile'];
      this.selectedGroup = null;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/application-manager/tabs/partials/jModalCreateApp.vue